import { Track } from "@/src/lib/tracking";
import { PayChannel, type WalletSign } from "@api/memberApi";
import { wrapperResult } from "@api/osp";

import { useContext } from "react";
import { getDeviceType, getGaInfoForReporting } from "utils";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";

export const usePayment = () => {
  const { memberApi } = useContext(OspApiClientContext);

  const handlePaymentCheck = async (planId: string) => {
    try {
      if (!memberApi?.payment?.paymentCheck) {
        throw new Error("Payment check client is not available");
      }
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      const res = await memberApi.payment.paymentCheck(
        {
          product_id: planId,
          pay_channel: PayChannel.STRIPE,
          payment_context: {
            return_url: `${window.location.origin}${window.location.pathname}?payment=success&planId=${planId}`,
            cancel_url: `${window.location.origin}${window.location.pathname}?payment=cancel&planId=${planId}`,
          },
        },
        {
          headers,
        },
      );
      return {
        data: res.data?.data?.offer_details?.[0]?.links?.[0]?.href,
        error: null,
      };
    } catch (error: unknown) {
      const err = error as { code?: string; msg?: string; data?: unknown };
      Track.log("error", "getPaymentCheck error", "getPaymentCheck error", {
        arg1: String(error),
        arg2: String(planId),
      });
      return {
        data: null,
        error: {
          code: err.code,
          msg: err.msg,
          data: err.data,
        },
      };
    }
  };

  const handleWalletSign = async ({
    planId,
    eoa_address,
    chain_id,
    token_address,
    sign_type,
    type,
  }: {
    planId?: string;
    eoa_address: string;
    chain_id: number;
    token_address?: string;
    sign_type?: string;
    type?: string;
  }) => {
    try {
      if (!memberApi?.wallet?.sign) {
        throw new Error("Wallet sign client is not available");
      }
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      const res = await wrapperResult<WalletSign>(
        memberApi.wallet.sign(
          {
            eoa_address,
            product_id: planId,
            chain_id,
            token_address,
            type: type || "EDGEN_SUB",
            sign_type: sign_type || "PAYMENT",
          },
          {
            headers,
          },
        ),
      );
      return {
        data: res,
        error: null,
      };
    } catch (error: unknown) {
      const err = error as {
        data?: { code?: string; msg?: string; data?: unknown };
      };
      Track.log("error", "walletSign error", "walletSign error", {
        arg1: String(err?.data),
        arg2: String(err?.data?.code),
        arg3: String(planId),
      });
      return {
        data: null,
        error: {
          code: err.data?.code,
          msg: err.data?.msg,
          data: err.data?.data,
        },
      };
    }
  };

  return {
    handleWalletSign,
    handlePaymentCheck,
  };
};
